/**
 * SWIFT MX Message Parser for pacs.008 and pacs.009
 * Parses SWIFT XML messages and extracts route information
 * Supports multiple messages in a single file
 */

/**
 * Parse SWIFT MX XML and extract route data
 * Supports both single and multiple Document elements
 * @param {string} xmlContent - The XML content as string
 * @returns {object} - Routes object in the format expected by the API
 */
export const parseSwiftXML = (xmlContent) => {
  const parser = new DOMParser();
  const serializer = new XMLSerializer();
  
  // Handle multiple XML declarations by splitting and processing separately
  const xmlDeclarationPattern = /<\?xml[^?]*\?>/g;
  const declarations = xmlContent.match(xmlDeclarationPattern) || [];
  
  // Store raw XML strings per document
  let allDocuments = [];  // {element, rawXml}
  
  if (declarations.length > 1) {
    // Multiple XML documents - split and parse each
    const parts = xmlContent.split(xmlDeclarationPattern).filter(part => part.trim());
    
    parts.forEach((part, idx) => {
      try {
        const fullXml = '<?xml version="1.0" encoding="UTF-8"?>' + part;
        const doc = parser.parseFromString(fullXml, 'text/xml');
        
        const parserError = doc.querySelector('parsererror');
        if (!parserError) {
          const docElement = doc.querySelector('Document');
          if (docElement) {
            allDocuments.push({ element: docElement, rawXml: fullXml.trim() });
          }
        }
      } catch (err) {
        console.warn(`Failed to parse message ${idx + 1}:`, err);
      }
    });
  } else {
    // Single document or no XML declaration
    const xmlDoc = parser.parseFromString(xmlContent, 'text/xml');
    const parserError = xmlDoc.querySelector('parsererror');
    
    if (parserError) {
      const wrappedContent = `<Messages>${xmlContent}</Messages>`;
      const wrappedDoc = parser.parseFromString(wrappedContent, 'text/xml');
      const wrappedError = wrappedDoc.querySelector('parsererror');
      
      if (wrappedError) {
        throw new Error('Invalid XML format');
      }
      
      const docs = wrappedDoc.querySelectorAll('Document');
      docs.forEach(docEl => {
        const rawXml = '<?xml version="1.0" encoding="UTF-8"?>\n' + serializer.serializeToString(docEl);
        allDocuments.push({ element: docEl, rawXml });
      });
    } else {
      const docs = xmlDoc.querySelectorAll('Document');
      if (docs.length === 1) {
        allDocuments.push({ element: docs[0], rawXml: xmlContent.trim() });
      } else {
        docs.forEach(docEl => {
          const rawXml = '<?xml version="1.0" encoding="UTF-8"?>\n' + serializer.serializeToString(docEl);
          allDocuments.push({ element: docEl, rawXml });
        });
      }
    }
  }
  
  if (allDocuments.length === 0) {
    throw new Error('No SWIFT Document elements found in XML');
  }
  
  const allRoutes = [];
  let messageCount = 0;
  
  // Process each document - tag each route with its source message XML
  allDocuments.forEach(({ element: doc, rawXml }, idx) => {
    try {
      const isPacs008 = doc.querySelector('FIToFICstmrCdtTrf');
      const isPacs009 = doc.querySelector('FICdtTrf');
      
      let routes = [];
      
      if (isPacs008) {
        const result = parsePacs008Message(doc);
        routes = result.routes;
        messageCount++;
      } else if (isPacs009) {
        const result = parsePacs009Message(doc);
        routes = result.routes;
        messageCount++;
      } else {
        console.warn(`Document ${idx + 1} is not a supported message type (pacs.008 or pacs.009)`);
        return;
      }
      
      // Attach source XML to each route
      routes.forEach(route => {
        route._sourceXml = rawXml;
      });
      
      allRoutes.push(...routes);
    } catch (err) {
      console.error(`Error parsing document ${idx + 1}:`, err);
    }
  });
  
  if (allRoutes.length === 0) {
    throw new Error('No valid routes extracted from SWIFT messages');
  }
  
  return { 
    routes: allRoutes,
    messageCount: messageCount,
    routeCount: allRoutes.length
  };
};

/**
 * Parse pacs.008 - FIToFICustomerCreditTransfer
 */
const parsePacs008Message = (documentElement) => {
  const routes = [];
  
  // Extract credit transfer transactions
  const transactions = documentElement.querySelectorAll('CdtTrfTxInf');
  
  transactions.forEach((txn, idx) => {
    const route = extractRouteFromTransaction(documentElement, txn, 'pacs.008');
    if (route) {
      routes.push(route);
    }
  });
  
  // If no transactions found, create a single route from the message
  if (routes.length === 0) {
    const route = extractRouteFromMessage(documentElement, 'pacs.008');
    if (route) {
      routes.push(route);
    }
  }
  
  return { routes };
};

/**
 * Parse pacs.009 - FinancialInstitutionCreditTransfer
 */
const parsePacs009Message = (documentElement) => {
  const routes = [];
  
  const transactions = documentElement.querySelectorAll('CdtTrfTxInf');
  
  transactions.forEach((txn, idx) => {
    const route = extractRouteFromTransaction(documentElement, txn, 'pacs.009');
    if (route) {
      routes.push(route);
    }
  });
  
  if (routes.length === 0) {
    const route = extractRouteFromMessage(documentElement, 'pacs.009');
    if (route) {
      routes.push(route);
    }
  }
  
  return { routes };
};

/**
 * Extract route information from a transaction
 */
const extractRouteFromTransaction = (xmlDoc, txn, messageType) => {
  // Extract intermediary agents
  const intermediaries = [];
  
  // Check for IntrmyAgt1, IntrmyAgt2, IntrmyAgt3
  const intrmyAgt1BIC = getTextContent(txn, 'IntrmyAgt1 > FinInstnId > BICFI');
  const intrmyAgt2BIC = getTextContent(txn, 'IntrmyAgt2 > FinInstnId > BICFI');
  const intrmyAgt3BIC = getTextContent(txn, 'IntrmyAgt3 > FinInstnId > BICFI');
  
  if (intrmyAgt1BIC) intermediaries.push(intrmyAgt1BIC);
  if (intrmyAgt2BIC) intermediaries.push(intrmyAgt2BIC);
  if (intrmyAgt3BIC) intermediaries.push(intrmyAgt3BIC);
  
  // Extract Creditor Agent (Correspondent Bank)
  const creditorAgentBIC = getTextContent(txn, 'CdtrAgt > FinInstnId > BICFI') || 
                          getTextContent(xmlDoc, 'GrpHdr > InstdAgt > FinInstnId > BICFI') ||
                          'UNKNOWN';
  
  // Extract Currency
  const currency = getTextContent(txn, 'IntrBkSttlmAmt[Ccy]') || 
                   getTextContent(txn, 'InstdAmt[Ccy]') || 
                   getTextContent(xmlDoc, 'GrpHdr > TtlIntrBkSttlmAmt[Ccy]') ||
                   'USD';
  
  // Extract Amount
  const amountStr = getTextContent(txn, 'IntrBkSttlmAmt') || 
                    getTextContent(txn, 'InstdAmt') ||
                    getTextContent(xmlDoc, 'GrpHdr > TtlIntrBkSttlmAmt') ||
                    '0';
  const amount = parseFloat(amountStr.replace(/[^0-9.]/g, '')) || 0;
  
  // Extract Debtor and Creditor countries for corridor
  const debtorCountry = getTextContent(txn, 'Dbtr > PstlAdr > Ctry') || 
                       getTextContent(xmlDoc, 'GrpHdr > InstgAgt > FinInstnId > PstlAdr > Ctry') ||
                       inferCountryFromBIC(getTextContent(txn, 'DbtrAgt > FinInstnId > BICFI')) ||
                       'IN';
  const creditorCountry = getTextContent(txn, 'Cdtr > PstlAdr > Ctry') || 
                         getTextContent(xmlDoc, 'GrpHdr > InstdAgt > FinInstnId > PstlAdr > Ctry') ||
                         inferCountryFromBIC(getTextContent(txn, 'CdtrAgt > FinInstnId > BICFI')) ||
                         'US';
  
  // Check for GPI indicator (UETR presence indicates GPI)
  const uetr = getTextContent(txn, 'UETRs') || getTextContent(txn, 'UETR');
  const gpi = uetr ? 1 : 0;
  
  // Determine priority
  const priority = getTextContent(txn, 'SttlmPrty') || 'NORM';
  const priorityValue = priority === 'HIGH' || priority === 'URGENT' ? 'HIGH' : 'NORMAL';
  
  // Calculate defaults based on intermediary count
  const intermediaryCount = intermediaries.length;
  const estimatedCost = 15 + (intermediaryCount * 5); // Base cost + per intermediary
  const estimatedTime = 2 + (intermediaryCount * 3); // Base time + per intermediary
  const slaScore = Math.max(0.7, 0.95 - (intermediaryCount * 0.1)); // Decreases with more intermediaries
  
  // Risk and Liquidity based on intermediary count
  const getRiskLevel = (count) => {
    if (count <= 1) return 'Low';
    if (count === 2) return 'Medium';
    return 'High';
  };
  
  const getLiquidityLevel = (count) => {
    if (count <= 1) return 'High';
    if (count === 2) return 'Medium';
    return 'Low';
  };
  
  const risk = getRiskLevel(intermediaryCount);
  const liquidity = getLiquidityLevel(intermediaryCount);
  
  return {
    CB_BIC: creditorAgentBIC,
    Intermediaries: intermediaryCount,
    Currency: currency,
    Amount: amount,
    SLA_Score: parseFloat(slaScore.toFixed(2)),
    Cost: estimatedCost,
    Avg_Time_Hours: estimatedTime,
    GPI: gpi,
    Liquidity: liquidity,
    Risk: risk,
    Priority: priorityValue,
    Corridor: `${debtorCountry}-${creditorCountry}`
  };
};

/**
 * Extract route information from message header when no transactions found
 */
const extractRouteFromMessage = (xmlDoc, messageType) => {
  // Extract from GroupHeader
  const creditorAgentBIC = getTextContent(xmlDoc, 'GrpHdr > InstdAgt > FinInstnId > BICFI') || 'UNKNOWN';
  const currency = getTextContent(xmlDoc, 'GrpHdr > TtlIntrBkSttlmAmt[Ccy]') || 'USD';
  const amountStr = getTextContent(xmlDoc, 'GrpHdr > TtlIntrBkSttlmAmt') || '0';
  const amount = parseFloat(amountStr.replace(/[^0-9.]/g, '')) || 0;
  
  return {
    CB_BIC: creditorAgentBIC,
    Intermediaries: 1, // Default to 1 intermediary
    Currency: currency,
    Amount: amount,
    SLA_Score: 0.85,
    Cost: 15,
    Avg_Time_Hours: 5,
    GPI: 0,
    Liquidity: 'Medium',
    Risk: 'Low',
    Priority: 'NORMAL',
    Corridor: 'US-IN'
  };
};

/**
 * Helper to get text content from XML selector
 */
const getTextContent = (parentElement, selector) => {
  // Handle attribute selectors like [Ccy]
  if (selector.includes('[') && selector.includes(']')) {
    const parts = selector.split('[');
    const elementPath = parts[0];
    const attrName = parts[1].replace(']', '');
    
    const element = parentElement.querySelector(elementPath);
    return element ? element.getAttribute(attrName) : null;
  }
  
  const element = parentElement.querySelector(selector);
  return element ? element.textContent.trim() : null;
};

/**
 * Infer country code from BIC code
 * BIC format: AAAABBCCXXX where BB is country code
 */
const inferCountryFromBIC = (bic) => {
  if (!bic || bic.length < 6) return null;
  return bic.substring(4, 6);  // Extract country code (positions 5-6)
};

/**
 * Generate sample SWIFT pacs.008 XML for testing
 */
export const getSampleSwiftXML = () => {
  return `<?xml version="1.0" encoding="UTF-8"?>
<Document xmlns="urn:iso:std:iso:20022:tech:xsd:pacs.008.001.08">
  <FIToFICstmrCdtTrf>
    <GrpHdr>
      <MsgId>MSGID123456</MsgId>
      <CreDtTm>2024-01-15T10:30:00</CreDtTm>
      <NbOfTxs>3</NbOfTxs>
      <TtlIntrBkSttlmAmt Ccy="USD">3000000</TtlIntrBkSttlmAmt>
    </GrpHdr>
    <CdtTrfTxInf>
      <PmtId>
        <InstrId>INSTR001</InstrId>
        <EndToEndId>E2E001</EndToEndId>
        <UETR>1234567890abcdef</UETR>
      </PmtId>
      <IntrBkSttlmAmt Ccy="USD">1000000</IntrBkSttlmAmt>
      <IntrmyAgt1>
        <FinInstnId>
          <BICFI>CHASUS33</BICFI>
        </FinInstnId>
      </IntrmyAgt1>
      <CdtrAgt>
        <FinInstnId>
          <BICFI>CITIUS33</BICFI>
        </FinInstnId>
      </CdtrAgt>
      <Dbtr>
        <PstlAdr>
          <Ctry>US</Ctry>
        </PstlAdr>
      </Dbtr>
      <Cdtr>
        <PstlAdr>
          <Ctry>IN</Ctry>
        </PstlAdr>
      </Cdtr>
    </CdtTrfTxInf>
    <CdtTrfTxInf>
      <PmtId>
        <InstrId>INSTR002</InstrId>
        <EndToEndId>E2E002</EndToEndId>
      </PmtId>
      <IntrBkSttlmAmt Ccy="USD">1000000</IntrBkSttlmAmt>
      <IntrmyAgt1>
        <FinInstnId>
          <BICFI>INTM001</BICFI>
        </FinInstnId>
      </IntrmyAgt1>
      <IntrmyAgt2>
        <FinInstnId>
          <BICFI>INTM002</BICFI>
        </FinInstnId>
      </IntrmyAgt2>
      <IntrmyAgt3>
        <FinInstnId>
          <BICFI>INTM003</BICFI>
        </FinInstnId>
      </IntrmyAgt3>
      <CdtrAgt>
        <FinInstnId>
          <BICFI>DEUTDEFF</BICFI>
        </FinInstnId>
      </CdtrAgt>
      <Dbtr>
        <PstlAdr>
          <Ctry>US</Ctry>
        </PstlAdr>
      </Dbtr>
      <Cdtr>
        <PstlAdr>
          <Ctry>IN</Ctry>
        </PstlAdr>
      </Cdtr>
    </CdtTrfTxInf>
    <CdtTrfTxInf>
      <PmtId>
        <InstrId>INSTR003</InstrId>
        <EndToEndId>E2E003</EndToEndId>
      </PmtId>
      <IntrBkSttlmAmt Ccy="USD">1000000</IntrBkSttlmAmt>
      <IntrmyAgt1>
        <FinInstnId>
          <BICFI>HSBC001</BICFI>
        </FinInstnId>
      </IntrmyAgt1>
      <IntrmyAgt2>
        <FinInstnId>
          <BICFI>HSBC002</BICFI>
        </FinInstnId>
      </IntrmyAgt2>
      <CdtrAgt>
        <FinInstnId>
          <BICFI>HSBCGB2L</BICFI>
        </FinInstnId>
      </CdtrAgt>
      <Dbtr>
        <PstlAdr>
          <Ctry>US</Ctry>
        </PstlAdr>
      </Dbtr>
      <Cdtr>
        <PstlAdr>
          <Ctry>IN</Ctry>
        </PstlAdr>
      </Cdtr>
    </CdtTrfTxInf>
  </FIToFICstmrCdtTrf>
</Document>`;
};
